import { useState, useCallback, useMemo } from 'react'
import {
  HandCoins, RotateCcw, Download, Info, Scale, ArrowRight, ChevronDown, HelpCircle,
} from 'lucide-react'
import '../cdg31.css'
import Cdg31Header from '../Cdg31Header'
import { Footer } from '../Cdg31Sections'

const BAREME = [
  { min: 0,  max: 10, rate: 1 / 4,  label: '1/4 de mois brut', desc: 'Jusqu\u2019à 10 ans' },
  { min: 10, max: 15, rate: 2 / 5,  label: '2/5 de mois brut', desc: 'De 10 à 15 ans' },
  { min: 15, max: 20, rate: 1 / 2,  label: '1/2 mois brut',    desc: 'De 15 à 20 ans' },
  { min: 20, max: 24, rate: 3 / 5,  label: '3/5 de mois brut', desc: 'De 20 à 24 ans' },
]

const MAX_YEARS = 24

function computeIndemnity(grossAnnual, seniorityYears) {
  if (!grossAnnual || grossAnnual <= 0 || !seniorityYears || seniorityYears <= 0) return null

  const monthlyGross = grossAnnual / 12
  const capped = Math.min(seniorityYears, MAX_YEARS)

  const breakdown = BAREME.map(tier => {
    const yearsInTier = Math.max(0, Math.min(capped, tier.max) - tier.min)
    return { ...tier, years: yearsInTier, amount: yearsInTier * tier.rate * monthlyGross }
  }).filter(t => t.years > 0)

  const minimum = breakdown.reduce((sum, t) => sum + t.amount, 0)
  const maximum = monthlyGross * capped

  return {
    monthlyGross,
    capped,
    breakdown,
    minimum: Math.round(minimum * 100) / 100,
    maximum: Math.round(maximum * 100) / 100,
  }
}

function fmt(n) {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function ExclusionsHelp() {
  const [open, setOpen] = useState(false)
  return (
    <div className="cdg-sim-help-box">
      <button type="button" className="cdg-sim-help-toggle" onClick={() => setOpen(v => !v)}
        aria-expanded={open}>
        <HelpCircle size={14} />
        Quels éléments sont exclus de la rémunération de référence ?
        <ChevronDown size={14} className={`chevron${open ? ' open' : ''}`} />
      </button>
      {open && (
        <div className="cdg-sim-help-content">
          <p>Sont exclus de la rémunération brute annuelle de référence :</p>
          <ul>
            <li>Les primes et indemnités ayant le caractère de <strong>remboursement de frais</strong></li>
            <li>Les majorations et indexations relatives à une <strong>affectation outre-mer</strong></li>
            <li>L'<strong>indemnité de résidence à l'étranger</strong></li>
            <li>Les primes et indemnités liées au <strong>changement de résidence</strong>, à la
              primo-affectation, à la mobilité géographique et aux restructurations</li>
            <li>Les <strong>indemnités d'enseignement ou de jury</strong> ainsi que les autres
              indemnités non directement liées à l'emploi</li>
          </ul>
          <p style={{ marginTop: '0.5rem', fontStyle: 'italic' }}>
            Pour les agents bénéficiant d'un <strong>logement pour nécessité absolue de service</strong>,
            le montant des primes et indemnités pris en compte est celui qu'ils auraient perçu s'ils
            n'avaient pas bénéficié d'un tel logement.
          </p>
        </div>
      )}
    </div>
  )
}

export default function Cdg31SimulateurRupture() {
  const [grossAnnual, setGrossAnnual] = useState('')
  const [seniority, setSeniority] = useState('')

  const result = useMemo(
    () => computeIndemnity(parseFloat(grossAnnual), parseInt(seniority, 10)),
    [grossAnnual, seniority],
  )

  const handleReset = useCallback(() => {
    setGrossAnnual('')
    setSeniority('')
  }, [])

  const hasData = grossAnnual !== '' || seniority !== ''

  return (
    <div className="cdg31-page">
      <Cdg31Header onOpenChat={() => {}} />

      <div className="cdg-sim-breadcrumb">
        <div className="cdg-container">
          <a href="/cdg31">Accueil</a>
          <span className="sep">/</span>
          <a href="/cdg31">Ressources</a>
          <span className="sep">/</span>
          <span>Simulateur indemnité de rupture conventionnelle</span>
        </div>
      </div>

      <main className="cdg-sim-page">
        <div className="cdg-container">

          <div className="cdg-sim-header">
            <div className="cdg-sim-icon-title teal" aria-hidden="true">
              <HandCoins size={28} />
            </div>
            <h1>Simulateur de l'indemnité spécifique de rupture conventionnelle</h1>
            <p>
              Déterminez la fourchette minimale et maximale de l'indemnité spécifique
              de rupture conventionnelle, à partir de votre rémunération brute annuelle
              de référence et de votre ancienneté dans la fonction publique.
            </p>
          </div>

          {/* ═══ Saisie ═══ */}
          <div className="cdg-sim-card">
            <div className="cdg-sim-card-title">
              <span className="icon-circle blue" aria-hidden="true"><HandCoins size={18} /></span>
              1. Éléments de calcul
            </div>

            <div className="cdg-sim-input-row">
              <div className="cdg-sim-field">
                <label htmlFor="sim-gross">
                  Rémunération brute annuelle <span className="req">*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input id="sim-gross" type="number" className="cdg-sim-input"
                    placeholder="Ex : 28 000" min="0" step="100"
                    value={grossAnnual} onChange={e => setGrossAnnual(e.target.value)}
                    style={{ paddingRight: '2.5rem' }} aria-describedby="sim-gross-hint" />
                  <span style={{
                    position: 'absolute', right: '0.85rem', top: '50%',
                    transform: 'translateY(-50%)', color: 'var(--fg-faint)',
                    fontWeight: 600, fontSize: 'var(--fs-sm)', pointerEvents: 'none',
                  }}>€</span>
                </div>
                <span id="sim-gross-hint" className="cdg-sim-input-hint">
                  Rémunération brute perçue au cours de l'année civile précédant
                  la date d'effet de la rupture conventionnelle.
                </span>
                <ExclusionsHelp />
              </div>

              <div className="cdg-sim-field">
                <label htmlFor="sim-seniority">
                  Ancienneté (années complètes) <span className="req">*</span>
                </label>
                <input id="sim-seniority" type="number" className="cdg-sim-input"
                  placeholder="Ex : 12" min="0" max="50" step="1"
                  value={seniority} onChange={e => setSeniority(e.target.value)}
                  aria-describedby="sim-seniority-hint" />
                <span id="sim-seniority-hint" className="cdg-sim-input-hint">
                  Durée des services effectifs accomplis dans la fonction publique
                  de l'État, territoriale et hospitalière.
                  {parseInt(seniority, 10) > MAX_YEARS && (
                    <strong style={{ color: 'var(--cdg-yellow-dark)', display: 'block', marginTop: '0.25rem' }}>
                      Plafonnée à {MAX_YEARS} ans pour le calcul.
                    </strong>
                  )}
                </span>
              </div>
            </div>

            {result && (
              <div className="cdg-sim-derived" aria-live="polite">
                <span>Soit 1 mois de rémunération brute :</span>
                <span className="derived-val">{fmt(result.monthlyGross)} €</span>
              </div>
            )}
          </div>

          {/* ═══ Détail du calcul ═══ */}
          {result && result.breakdown.length > 0 && (
            <div className="cdg-sim-card">
              <div className="cdg-sim-card-title">
                <span className="icon-circle yellow" aria-hidden="true"><Scale size={18} /></span>
                2. Détail du calcul — montant minimum
              </div>

              <div className="cdg-sim-breakdown">
                <table>
                  <thead>
                    <tr>
                      <th>Tranche d'ancienneté</th>
                      <th>Taux applicable</th>
                      <th style={{ textAlign: 'center' }}>Années retenues</th>
                      <th>Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.breakdown.map((tier, i) => (
                      <tr key={i}>
                        <td>{tier.desc}</td>
                        <td>{tier.label}</td>
                        <td style={{ textAlign: 'center' }}>{tier.years}</td>
                        <td>{fmt(tier.amount)} €</td>
                      </tr>
                    ))}
                    <tr className="row-total">
                      <td colSpan={2}>
                        Montant minimum de l'indemnité
                      </td>
                      <td style={{ textAlign: 'center' }}>{result.capped} an{result.capped > 1 ? 's' : ''}</td>
                      <td>{fmt(result.minimum)} €</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 'var(--space-4)' }}>
                <div className="cdg-sim-derived">
                  <span>Montant maximum : 1/12 de rémunération brute annuelle × {result.capped} an{result.capped > 1 ? 's' : ''} =</span>
                  <span className="derived-val">{fmt(result.maximum)} €</span>
                </div>
                <span className="cdg-sim-input-hint">
                  Le montant maximum ne peut excéder 1/12 de la rémunération brute annuelle
                  par année d'ancienneté, dans la limite de {MAX_YEARS} ans.
                </span>
              </div>
            </div>
          )}

          {/* ═══ Résultat fourchette ═══ */}
          {result && (
            <div className="cdg-sim-result" role="status" aria-live="polite">
              <div className="cdg-sim-result-title">3. Fourchette de l'indemnité spécifique</div>
              <div className="cdg-sim-result-range">
                <div className="cdg-sim-result-item">
                  <div className="cdg-sim-result-value">
                    {fmt(result.minimum)}<span className="unit">€</span>
                  </div>
                  <div className="cdg-sim-result-label">Montant minimum légal</div>
                </div>
                <span className="range-arrow" aria-hidden="true"><ArrowRight size={28} /></span>
                <div className="cdg-sim-result-item">
                  <div className="cdg-sim-result-value">
                    {fmt(result.maximum)}<span className="unit">€</span>
                  </div>
                  <div className="cdg-sim-result-label">Montant maximum autorisé</div>
                </div>
              </div>
            </div>
          )}

          {result && (
            <div className="cdg-sim-alerts">
              <div className="cdg-sim-alert info">
                <Info size={16} className="alert-icon" />
                <span>
                  Selon l'accord entre l'agent et l'administration, le montant de l'indemnité
                  spécifique qui figurera dans la convention peut être établi
                  entre <strong>{fmt(result.minimum)} €</strong> et <strong>{fmt(result.maximum)} €</strong>.
                </span>
              </div>
            </div>
          )}

          {/* ═══ Toolbar ═══ */}
          <div className="cdg-sim-toolbar">
            <button className="cdg-btn cdg-btn-outline" onClick={handleReset} disabled={!hasData}>
              <RotateCcw size={15} /> Réinitialiser
            </button>
            <a className="cdg-btn cdg-btn-primary"
              href="/assets/cdg31/simulateurs/Simulateur_indemnite_rupture_conventionnelle_CDGOccitanie.xlsx" download>
              <Download size={15} /> Télécharger le fichier Excel
            </a>
          </div>

          {/* ═══ Legal ═══ */}
          <div className="cdg-sim-legal" aria-label="Références réglementaires">
            <div className="cdg-sim-legal-title"><Scale size={16} /> Références réglementaires</div>
            <p>
              Simulateur proposé par les Centres de gestion de la fonction publique
              territoriale en Occitanie. Fiche établie sous la responsabilité de saisie
              d'informations par l'employeur qui a pris soin de vérifier les éléments
              réglementaires.
            </p>
            <ul>
              <li>
                <strong>Loi n° 2019-828 du 6 août 2019</strong> — article 72, de transformation
                de la fonction publique.
              </li>
              <li>
                <strong>Décret n° 2019-1593 du 31 décembre 2019</strong> relatif à la procédure
                de rupture conventionnelle dans la fonction publique.
              </li>
              <li>
                <strong>Décret n° 2019-1596 du 31 décembre 2019</strong> relatif à l'indemnité
                spécifique de rupture conventionnelle dans la fonction publique.
              </li>
            </ul>
          </div>

          <div className="cdg-sim-alerts">
            <div className="cdg-sim-alert info">
              <Info size={16} className="alert-icon" />
              <span>
                <strong>Convention de calcul POC :</strong> montant minimum calculé par tranche
                (1/4, 2/5, 1/2, 3/5 de mois brut), montant maximum = 1/12 de la rémunération
                brute annuelle × ancienneté (plafond {MAX_YEARS} ans). À ajuster après validation
                du fichier Excel source.
              </span>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  )
}
